import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, KeyRound, Mail, Shield, Eye, Clock, GraduationCap, Monitor, Copy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import OnboardingTour from "@/components/OnboardingTour";
import WelcomeIntro from "@/components/WelcomeIntro";

export default function ProfilePage() {
  const { user } = useAuth();
  const { t } = useTranslation("app");
  const [tourOpen, setTourOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) {
      toast.error("La nuova password deve essere di almeno 6 caratteri");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("La conferma password non coincide");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password cambiata con successo");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Cambio password non riuscito";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-3xl font-heading font-black tracking-tight">
        Il mio profilo
      </h1>

      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <WelcomeIntro open={introOpen} onClose={() => setIntroOpen(false)} />

      {/* Tutorial */}
      <Card className="p-5 flex items-center justify-between gap-4" data-testid="profile-tour-card">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-5 w-5 text-[#0044FF]" />
          <div>
            <div className="font-heading font-bold text-sm">{t("tour.profileTitle")}</div>
            <div className="text-xs text-slate-500">
              {t("tour.profileSub")}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setTourOpen(true)}
          data-testid="profile-rewatch-tour-button"
        >
          {t("tour.profileBtn")}
        </Button>
      </Card>

      {/* Iter 78: rivedi presentazione animata */}
      <Card className="p-5 flex items-center justify-between gap-4" data-testid="profile-intro-card">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-5 w-5 text-[#0044FF]" />
          <div>
            <div className="font-heading font-bold text-sm">{t("intro.profileTitle")}</div>
            <div className="text-xs text-slate-500">{t("intro.profileSub")}</div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setIntroOpen(true)}
          data-testid="profile-rewatch-intro-button"
        >
          {t("intro.profileBtn")}
        </Button>
      </Card>

      {/* Usala anche sul computer */}
      <Card className="p-5 space-y-3" data-testid="profile-pc-help-card">
        <div className="flex items-center gap-3">
          <Monitor className="h-5 w-5 text-[#0044FF]" />
          <div>
            <div className="font-heading font-bold text-sm">{t("pcHelp.title")}</div>
            <div className="text-xs text-slate-500">{t("pcHelp.sub")}</div>
          </div>
        </div>
        <ol className="space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
          <li>{t("pcHelp.step1")}</li>
          <li>{t("pcHelp.step2")}</li>
          <li>{t("pcHelp.step3")}</li>
          <li>{t("pcHelp.step4")}</li>
        </ol>
        <div className="flex flex-wrap items-center gap-2">
          <code
            className="text-xs font-mono bg-slate-100 border border-slate-200 rounded px-2 py-1.5 truncate max-w-full"
            data-testid="profile-pc-help-url"
          >
            {window.location.origin}
          </code>
          <Button
            variant="outline"
            size="sm"
            data-testid="profile-pc-help-copy-button"
            onClick={() => {
              navigator.clipboard.writeText(window.location.origin);
              toast.success(t("pcHelp.copied"));
            }}
          >
            <Copy className="h-4 w-4 mr-1.5" /> {t("pcHelp.copyBtn")}
          </Button>
        </div>
      </Card>

      {/* Dati account */}
      <Card className="p-5 space-y-4" data-testid="profile-info-card">
        <div className="font-heading font-bold flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#0044FF]" /> Dati account
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-3 gap-x-4 text-sm">
          <div className="font-bold text-slate-600 flex items-center gap-1.5">
            <Mail className="h-4 w-4" /> Email
          </div>
          <div>{user.email}</div>
          <div className="font-bold text-slate-600">Nome</div>
          <div>{user.name || "—"}</div>
          <div className="font-bold text-slate-600">Ruolo</div>
          <div className="uppercase tracking-wide">
            {user.role === "admin" ? "Amministratore" : "Utente"}
          </div>
          <div className="font-bold text-slate-600 flex items-center gap-1.5">
            <Eye className="h-4 w-4" /> Visibilità rapportini
          </div>
          <div>
            {user.can_view_all_rapportini
              ? "Vede tutti i rapportini"
              : "Vede solo i propri rapportini"}
          </div>
          {user.hours_cap ? (
            <>
              <div className="font-bold text-slate-600 flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> Tetto ore
              </div>
              <div className="text-amber-700 font-bold">
                Max {user.hours_cap}h/dipendente/giorno (visualizzazione filtrata)
              </div>
            </>
          ) : null}
        </div>
      </Card>

      {/* Cambio password */}
      <Card className="p-5" data-testid="change-password-card">
        <div className="font-heading font-bold flex items-center gap-2 mb-4">
          <KeyRound className="h-5 w-5 text-[#0044FF]" /> Cambia password
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <Label htmlFor="current-password">Password attuale</Label>
            <Input
              id="current-password"
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              required
              className="h-11 mt-1"
              autoComplete="current-password"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label htmlFor="new-password">Nuova password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
              minLength={6}
              className="h-11 mt-1"
              autoComplete="new-password"
              data-testid="new-password-input"
            />
            <p className="text-xs text-slate-500 mt-1">Almeno 6 caratteri.</p>
          </div>
          <div>
            <Label htmlFor="confirm-password">Conferma nuova password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              required
              className="h-11 mt-1"
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#0044FF] hover:bg-[#0033CC] h-11"
            data-testid="change-password-button"
          >
            <Lock className="h-4 w-4 mr-2" />
            {saving ? "Salvataggio…" : "Cambia password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
